import { Drawer, Input, Select, DatePicker, Button, message, Space, Tag, Grid } from "antd";
import { CalendarOutlined, FlagOutlined, LinkOutlined, FileTextOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { createTasksApi, fetchTaskTypeApi } from "../../../../apiservice/crm";
import { formatDayjsToUtc } from "../../../../lib/functions";
import "../../crmSharedStyle.css";
import "../crmTasksStyle.css";

const { TextArea } = Input;
const { useBreakpoint } = Grid;

const PRIORITY_OPTIONS = [
  { label: "High", value: "high", color: "red" },
  { label: "Medium", value: "medium", color: "orange" },
  { label: "Low", value: "low", color: "green" },
];

// The same suggestion the backend falls back to when a task is created
// without an explicit priority — kept here purely so the drawer can show a
// sensible default before the user picks one themselves.
const suggestPriority = (dueDate) => {
  if (!dueDate) return "low";
  const d = dayjs(dueDate);
  if (d.isBefore(dayjs(), "day")) return "high";
  if (d.isSame(dayjs(), "day")) return "medium";
  return "low";
};

const emptyForm = () => ({
  lead_id: null,
  deal_id: null,
  type: "followup",
  due_date: dayjs().add(1, "day"),
  remarks: "",
  priority: null, // null = "follow the due-date suggestion"; a user pick overrides it
});

const FieldLabel = ({ icon, children }) => (
  <div className="crm-field-label">
    {icon}
    {children}
  </div>
);

const TaskDrawer = ({ open, onClose, defaultLeadId, defaultDealId, leads = [], deals = [], onSuccess }) => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [taskTypeOptions, setTaskTypeOptions] = useState([]);

  const filteredDeals = deals.filter((d) => d.contact_id === form.lead_id);

  useEffect(() => {
    if (!open) return;

    fetchTaskTypeApi()
      .then((res) =>
        setTaskTypeOptions(
          (res.data || []).map((t) => ({ label: t.name, value: t.code }))
        )
      )
      .catch(() => message.error("Failed to load task types"));

    let leadId = defaultLeadId || null;
    let dealId = defaultDealId || null;

    if (defaultDealId) {
      const foundDeal = deals.find((d) => d.deal_id === defaultDealId);
      if (foundDeal) leadId = foundDeal.contact_id;
    }

    setForm({ ...emptyForm(), lead_id: leadId, deal_id: dealId });
  }, [open, defaultLeadId, defaultDealId, deals]);

  // What the priority tag/select shows: the user's own pick if they made
  // one, otherwise the live due-date suggestion.
  const effectivePriority = form.priority || suggestPriority(form.due_date);

  const handleSubmit = async () => {
    if (!form.lead_id && !form.deal_id) {
      return message.warning("Select a lead or deal first");
    }
    if (!form.due_date) {
      return message.warning("Select a due date");
    }

    setLoading(true);
    try {
      await createTasksApi({
        ...form,
        priority: effectivePriority,
        due_date: formatDayjsToUtc(form.due_date),
      });

      message.success("Task created");
      setForm(emptyForm());
      onClose();
      onSuccess?.();
    } catch (err) {
      message.error("Failed to create task");
    } finally {
      setLoading(false);
    }
  };

  const selectedLead = leads.find((l) => l.id === form.lead_id);

  return (
    <Drawer
      title="Create Task"
      open={open}
      destroyOnClose
      onClose={() => {
        setForm(emptyForm());
        onClose();
      }}
      width={isMobile ? "100%" : 420}
      className="crm-task-drawer"
      styles={{
          wrapper: {
            paddingTop: !screens.md ? "80px" : undefined
          }
        }}
    >
      <div className="crm-form-section">
        <FieldLabel icon={<LinkOutlined />}>Lead</FieldLabel>
        <Select
          placeholder="Select a lead"
          notFoundContent="No leads found"
          style={{ width: "100%" }}
          showSearch
          optionFilterProp="label"
          value={form.lead_id}
          onChange={(v) => setForm((s) => ({ ...s, lead_id: v, deal_id: null }))}
          options={leads.map((l) => ({
            label: `${l.name} (${l.phone})`,
            value: l.id,
          }))}
        />
        {selectedLead && (
          <div className="crm-selected-chip">
            Selected: <b>{selectedLead.name}</b>
          </div>
        )}
      </div>

      <div className="crm-form-section">
        <FieldLabel icon={<LinkOutlined />}>Deal (optional)</FieldLabel>
        <Select
          placeholder={form.lead_id ? "Select a deal" : "Select a lead first"}
          style={{ width: "100%" }}
          value={form.deal_id}
          onChange={(v) => setForm((s) => ({ ...s, deal_id: v }))}
          options={filteredDeals.map((d) => ({ label: d.title, value: d.deal_id }))}
          disabled={!form.lead_id}
        />
      </div>

      <div className="crm-form-row">
        <div className="crm-form-section">
          <FieldLabel icon={<FileTextOutlined />}>Type</FieldLabel>
          <Select
            value={form.type}
            onChange={(v) => setForm((s) => ({ ...s, type: v }))}
            style={{ width: "100%" }}
            options={taskTypeOptions}
          />
        </div>

        <div className="crm-form-section">
          <FieldLabel icon={<FlagOutlined />}>
            Priority
            {!form.priority && (
              <Tag className="crm-auto-tag" bordered={false}>
                auto
              </Tag>
            )}
          </FieldLabel>
          <Select
            style={{ width: "100%" }}
            value={effectivePriority}
            onChange={(v) => setForm((s) => ({ ...s, priority: v }))}
            options={PRIORITY_OPTIONS.map((o) => ({
              value: o.value,
              label: (
                <Space size={6}>
                  <span className={`crm-priority-dot ${o.value}`} />
                  {o.label}
                </Space>
              ),
            }))}
          />
        </div>
      </div>

      <div className="crm-form-section">
        <FieldLabel icon={<CalendarOutlined />}>Due date</FieldLabel>
        <DatePicker
          showTime={{ format: "HH:mm" }}
          format="YYYY-MM-DD HH:mm"
          style={{ width: "100%" }}
          value={form.due_date}
          onChange={(v) => setForm((s) => ({ ...s, due_date: v ? v.second(0) : v }))}
        />
        <div className="crm-field-hint">
          Priority defaults to <b>{PRIORITY_OPTIONS.find((o) => o.value === suggestPriority(form.due_date))?.label}</b> for
          this date unless you set it yourself above.
        </div>
      </div>

      <div className="crm-form-section">
        <FieldLabel icon={<FileTextOutlined />}>Remarks</FieldLabel>
        <TextArea
          className="crm-user-text"
          rows={3}
          placeholder="Add any context for this task"
          value={form.remarks}
          onChange={(e) => setForm((s) => ({ ...s, remarks: e.target.value }))}
        />
      </div>

      <Button
        type="primary"
        block
        size="large"
        loading={loading}
        onClick={handleSubmit}
        disabled={!form.lead_id && !form.deal_id}
        className="crm-drawer-submit"
      >
        Create Task
      </Button>
    </Drawer>
  );
};

export default TaskDrawer;
